import React, { useState, useRef, useEffect, useCallback } from 'react'

// types
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'
import {
  IEcommerceAnswerSchema,
  IFileAsset,
  IEcommerceVideoAsset,
  ASSETS_TYPES
} from '@aytm/app/swidget/base/questions/Ecommerce/types.ts'

// functions
import { applyFilter } from '@aytm/app/swidget/base/questions/Ecommerce/utils/applyFilter.ts'
import { defaultAssetIndex } from '@aytm/app/swidget/base/questions/Ecommerce/utils/defaultAssetIndex.ts'
import { getAssetType } from '@aytm/app/swidget/base/questions/Ecommerce/utils/getAssetType.ts'
import { getMediumAssetPath } from '@aytm/lib/common/assets.ts'

// custom hooks
import useTimeline from '../../../modules/timeline/useTimeline'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '../../../modules/viewport/useViewport'
import useAssetsCollectionBuilder from '../hooks/useAssetsCollectionBuilder.ts'

// components
import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import NikeVideo from './NikeVideo'
import CarouselVideo from './CarouselVideo'

// styles
import style from './Carousel.module.scss'

interface ICarouselProps {
  schema: IEcommerceAnswerSchema
  store: 'amazon' | 'walmart' | 'target' | 'default' | 'nike' | 'standart' | 'chewy' | 'costco'
  pagination: 'amazon' | 'walmart' | 'target' | 'default' | 'costco' | 'none'
  effect?: 'resizing'
  controls?: boolean
  custom?: boolean
  smallPagination?: boolean
  ignoreDefault?: boolean
  hasVideos?: boolean
  logEvents?: boolean
  openDialog?: (assetIndex: number) => void
}

const Carousel = ({
  schema,
  store = 'default',
  pagination = 'none',
  effect,
  controls = true,
  smallPagination = false,
  ignoreDefault = false,
  hasVideos = false,
  logEvents = true,
  openDialog
}: ICarouselProps) => {
  // custom hooks calls
  const { t } = useSurveyLocale()
  const timeline = useTimeline()
  const viewport = useViewport()

  // memoized
  const assetsCollection = useAssetsCollectionBuilder(schema)

  // state
  const [activeIndex, setActiveIndex] = useState<number>(() =>
    ignoreDefault ? 0 : defaultAssetIndex(assetsCollection)
  )

  // refs
  const activeIndexRef = useRef(activeIndex)
  activeIndexRef.current = activeIndex
  const carouselRef = useRef<HTMLDivElement>(null)

  //functions
  const onScroll = (event: React.UIEvent<HTMLElement>) => {
    const target = event.target as HTMLDivElement
    const scrollLeft = target.scrollLeft
    const itemWidth = target.offsetWidth
    const newIndex = Math.round(scrollLeft / itemWidth)
    if (newIndex !== activeIndex) {
      setActiveIndex(newIndex)
    }
  }
  const scrollToIndex = useCallback((index: number) => {
    if (carouselRef.current) {
      const itemWidth = carouselRef.current.offsetWidth
      carouselRef.current.scrollTo({
        left: itemWidth * index,
        behavior: 'auto'
      })
      setActiveIndex(index)
    }
  }, [])

  // effects
  useEffect(() => {
    if (carouselRef.current) {
      const itemWidth = carouselRef.current.offsetWidth
      carouselRef.current.scrollTo({
        left: itemWidth * activeIndexRef.current,
        behavior: 'auto'
      })
    }
  }, [])
  useEffect(() => {
    if (logEvents) {
      timeline.log({
        ref: schema.id,
        type: 'show_asset',
        context:
          ASSETS_TYPES.VIDEO === getAssetType(assetsCollection[activeIndex])
            ? ASSETS_TYPES.VIDEO
            : ASSETS_TYPES.IMAGE === getAssetType(assetsCollection[activeIndex])
              ? ASSETS_TYPES.IMAGE
              : ASSETS_TYPES.MODEL.toLowerCase(),
        target: activeIndex.toString()
      })
    }
  }, [activeIndex, timeline, schema.id, assetsCollection, logEvents])

  // render
  return (
    <div className={style.CarouselWrapper}>
      {'amazon' === store && controls && (
        <>
          <div className={style.aHeart}>
            <EmptyLink>
              <span className={style.aIcon}></span>
            </EmptyLink>
          </div>
          <div
            className={style.aTool}
            style={!assetsCollection.length || 1 === assetsCollection.length ? { bottom: '10px' } : { bottom: '34px' }}
          >
            <EmptyLink>
              <span className={style.aIcon}></span>
            </EmptyLink>
          </div>
        </>
      )}
      {'walmart' === store && controls && (
        <div className={style.wIcons}>
          <EmptyLink color="#0053e2">
            <div className={style.icon}>
              <i className="wm-Share" />
            </div>
          </EmptyLink>
          <EmptyLink color="#0053e2">
            <div className={style.icon}>
              <i className="wm-Heart" />
            </div>
          </EmptyLink>
          <button
            disabled={ASSETS_TYPES.VIDEO === getAssetType(assetsCollection[activeIndex])}
            className={style.icon}
            onClick={() => openDialog?.(activeIndex)}
          >
            <i className="wm-ZoomIn" />
          </button>
          {hasVideos && (
            <button
              disabled={ASSETS_TYPES.VIDEO !== getAssetType(assetsCollection[activeIndex])}
              className={style.icon}
              onClick={() => openDialog?.(activeIndex)}
            >
              <i className="wm-Play" />
            </button>
          )}
        </div>
      )}

      {'target' === store && controls && (
        <div className={style.tIcons}>
          <EmptyLink color="#CC0000">
            <div className={style.tHeart}></div>
          </EmptyLink>
        </div>
      )}

      {'chewy' === store && controls && !viewport.mobile && assetsCollection.length > 1 && (
        <>
          <button
            type="button"
            className={`${style.chewyArrow} ${style.chewyArrowLeft}`}
            disabled={activeIndex === 0}
            onClick={e => {
              e.stopPropagation()
              scrollToIndex(Math.max(0, activeIndex - 1))
            }}
            aria-label={t('questions', 'ec', 'chewy', 'previous')}
          >
            <i className="pi pi-chevron-left" />
          </button>
          <button
            type="button"
            className={`${style.chewyArrow} ${style.chewyArrowRight}`}
            disabled={activeIndex === assetsCollection.length - 1}
            onClick={e => {
              e.stopPropagation()
              scrollToIndex(Math.min(assetsCollection.length - 1, activeIndex + 1))
            }}
            aria-label={t('questions', 'ec', 'chewy', 'next')}
          >
            <i className="pi pi-chevron-right" />
          </button>
        </>
      )}

      <div
        className={style.Carousel}
        style={{ boxShadow: 'target' === store ? '0 1px 2px 1px rgba(0, 0, 0, 0.1490196078)' : '' }}
        onScroll={onScroll}
        ref={carouselRef}
      >
        {assetsCollection.length ? (
          <>
            {assetsCollection.map((asset, index) => (
              <div
                className={style.carouselItem}
                key={index}
                onClick={'costco' === store ? () => openDialog?.(index) : undefined}
              >
                {ASSETS_TYPES.IMAGE === getAssetType(asset) && (
                  <div
                    className={`
                      ${style[`${store}Item`]}
                      ${'resizing' === effect && index === activeIndex ? style.targetItemActive : ''}
                      ${
                        'nike' === store && false === schema.not_apply_filter && applyFilter(asset as IImageAsset)
                          ? style.nikeItemScaled
                          : ''
                      }
                    `}
                    role="img"
                    aria-label={(asset as IImageAsset).alt_text || t('questions', 'img', 'noAlt')}
                    style={{
                      backgroundImage: `url(${getMediumAssetPath(asset as IImageAsset)})`
                    }}
                  />
                )}
                {ASSETS_TYPES.MODEL === getAssetType(asset) && (
                  <div className={style.modelPreviewWrapper}>
                    <model-viewer
                      src={(asset as IFileAsset).file_url}
                      camera-controls={false}
                      interaction-prompt="none"
                    />
                    <div className={style.modelPreview} />
                  </div>
                )}
                {ASSETS_TYPES.VIDEO === getAssetType(asset) && (
                  <>
                    {'walmart' === store && (
                      <div
                        className={style.walmartVideo}
                        role="presentation"
                        style={{ backgroundImage: `url(${(asset as IEcommerceVideoAsset).video.thumbnail})` }}
                      />
                    )}
                    {'default' === store && (
                      <div
                        className={style.defaultVideo}
                        role="presentation"
                        style={{ backgroundImage: `url(${(asset as IEcommerceVideoAsset).video.thumbnail})` }}
                      />
                    )}
                    {('target' === store || 'standart' === store || 'amazon' === store) && (
                      <CarouselVideo
                        answer={schema}
                        assetIndex={index}
                        asset={asset as IEcommerceVideoAsset}
                        store={store}
                      />
                    )}
                    {'nike' === store && (
                      <NikeVideo asset={asset as IEcommerceVideoAsset} answer={schema} assetIndex={index} />
                    )}
                    {'chewy' === store && (
                      <div
                        className={style.chewyVideo}
                        style={{ backgroundImage: `url(${(asset as IEcommerceVideoAsset).video.thumbnail})` }}
                      />
                    )}
                    {'costco' === store && (
                      <div
                        className={style.costcoVideo}
                        role="presentation"
                        style={{ backgroundImage: `url(${(asset as IEcommerceVideoAsset).video.thumbnail})` }}
                      />
                    )}
                  </>
                )}
              </div>
            ))}
          </>
        ) : (
          <div className={`${style.Dumb} ${style[`${store}Dumb`]}`} />
        )}
      </div>
      {assetsCollection.length > 1 && 'costco' === pagination && (
        <div className={style.costcoCounter}>
          {activeIndex + 1}/{assetsCollection.length}
        </div>
      )}
      {assetsCollection.length > 1 && 'none' != pagination && 'costco' != pagination && (
        <div className={`${style[`${pagination}Pagination`]}`}>
          {assetsCollection.map((_, index) => (
            <div
              key={index}
              className={`
                ${style.Dot}
                ${index === activeIndex ? style.Active : ''}
                ${smallPagination ? style.DotSmall : ''}
              `}
              onClick={() => scrollToIndex(index)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default Carousel
